import { Field } from '@ark-ui/react';
import cn from 'classnames';
import { useEffect, useId, useRef, useState } from 'react';

import styles from './TextInput.module.scss';

const TEXTAREA_CHARACTER_LIMIT = 1000;
const TEXTAREA_CHARACTER_LIMIT_MESSAGE = 'You\u2019ve reached the 1,000 character count limit.';

export type TextInputProps = {
  type: 'text' | 'email' | 'textarea';
  label: string;
  placeholder: string;
  required?: boolean;
  className?: string;
};

const TextInput = (props: TextInputProps): JSX.Element => {
  const { type, label, placeholder, required, className } = props;
  const [isCharacterLimitReached, setIsCharacterLimitReached] = useState(false);
  const characterLimitMessageId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const form = textareaRef.current?.form;

    if (type !== 'textarea' || !form) {
      return;
    }

    const handleReset = () => {
      setIsCharacterLimitReached(false);
    };

    form.addEventListener('reset', handleReset);

    return () => {
      form.removeEventListener('reset', handleReset);
    };
  }, [type]);

  return (
    <Field.Root
      className={cn(styles.main, className)}
      invalid={type === 'textarea' && isCharacterLimitReached}
    >
      <Field.Label className={'visually-hidden'}>{label}</Field.Label>
      {type === 'textarea' ? (
        <>
          <Field.Textarea
            ref={textareaRef}
            className={cn(styles.textarea, 'body-med')}
            placeholder={placeholder + (required ? '*' : '')}
            required={required}
            name={label}
            maxLength={TEXTAREA_CHARACTER_LIMIT}
            aria-describedby={isCharacterLimitReached ? characterLimitMessageId : undefined}
            onChange={(event) => {
              setIsCharacterLimitReached(
                event.currentTarget.value.length >= TEXTAREA_CHARACTER_LIMIT
              );
            }}
          />
          {isCharacterLimitReached && (
            <Field.ErrorText id={characterLimitMessageId} className={cn(styles.error, 'body-sm')}>
              {TEXTAREA_CHARACTER_LIMIT_MESSAGE}
            </Field.ErrorText>
          )}
        </>
      ) : (
        <Field.Input
          className={cn(styles.input, 'body-med')}
          type={type}
          placeholder={placeholder + (required ? '*' : '')}
          required={required}
          name={label}
          pattern={
            type === 'email' ? '[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$' : undefined
          }
        />
      )}
    </Field.Root>
  );
};

export default TextInput;
